// 层级视图组件 - Apple Finder 风格 3 栏布局
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Plus,
  Edit2,
  ChevronRight,
  FolderOpen,
  Package,
  Box,
  Layers,
  Star,
  Image as ImageIcon,
} from 'lucide-react';
import type { Product, ProductModel, ProductCategory } from '@/poster/types';
import { ProductCategoryLabels } from '@/poster/types';
import type { ProductSeries } from '../hooks/useSeriesManager';
import type { NavigationState } from '../hooks/useHierarchyNavigation';
import { useColumnResize } from '../hooks/useColumnResize';
import { DEFAULT_COLUMN_WIDTHS } from '../constants';

interface HierarchyViewProps {
  // 导航状态
  navigation: NavigationState;
  
  // 数据
  currentSeriesList: ProductSeries[];
  currentProducts: Product[];
  categorySeriesCount: Record<string, number>;
  seriesProductCount: Record<string, number>;
  
  // 拖拽状态
  draggedProduct: Product | null;
  dropTargetSeries: string | null;
  
  // 导航回调
  onNavigateToCategory: (category: ProductCategory) => void;
  onNavigateToSeries: (series: ProductSeries) => void;
  onNavigateToProduct: (product: Product) => void;
  
  // 拖拽回调
  onDragStart: (e: React.DragEvent, product: Product) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent, seriesName: string) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, series: ProductSeries) => void;
  
  // 右键菜单回调
  onContextMenu: (
    e: React.MouseEvent,
    type: 'series' | 'product',
    item: ProductSeries | Product
  ) => void;
  
  // 操作回调
  onCreateSeries: () => void;
  onCreateProduct: () => void;
  onEditProduct: (product: Product) => void;
  onAddModel: (productId: string) => void;
  onEditModel: (productId: string, model: ProductModel) => void;
  onDeleteModel: (model: ProductModel) => void;
}

/**
 * 层级视图组件
 * Apple Finder 风格的 3 栏分层导航
 * 
 * 布局: [分类] → [系列] → [产品] → [详情]
 */
export function HierarchyView({
  navigation,
  currentSeriesList,
  currentProducts,
  categorySeriesCount,
  seriesProductCount,
  draggedProduct,
  dropTargetSeries,
  onNavigateToCategory,
  onNavigateToSeries,
  onNavigateToProduct,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onContextMenu,
  onCreateSeries,
  onCreateProduct,
  onEditProduct,
  onAddModel,
  onEditModel,
  onDeleteModel,
}: HierarchyViewProps) {
  // 列宽调整
  const { columnWidths, handleResizeStart } = useColumnResize(DEFAULT_COLUMN_WIDTHS);
  
  // 内联创建系列状态
  const [isCreatingSeriesInline, setIsCreatingSeriesInline] = useState(false);
  const [newSeriesName, setNewSeriesName] = useState('');
  const newSeriesInputRef = useRef<HTMLInputElement>(null);

  // 开始内联创建系列
  const startInlineSeriesCreation = useCallback(() => {
    setIsCreatingSeriesInline(true);
    setNewSeriesName('');
    setTimeout(() => newSeriesInputRef.current?.focus(), 50);
  }, []);

  // 取消内联创建
  const cancelInlineSeriesCreation = useCallback(() => {
    setIsCreatingSeriesInline(false);
    setNewSeriesName('');
  }, []);

  // 确认创建系列（由父组件处理实际创建）
  const handleCreateSeriesInline = useCallback(() => {
    if (!newSeriesName.trim()) {
      cancelInlineSeriesCreation();
      return;
    }
    // 触发父组件的创建逻辑
    onCreateSeries();
    cancelInlineSeriesCreation();
  }, [newSeriesName, onCreateSeries, cancelInlineSeriesCreation]);

  return (
    <div className="flex-1 flex flex-col bg-gray-100" style={{ height: 'calc(100vh - 73px)' }}>
      {/* 分栏视图容器 */}
      <div className="flex-1 flex overflow-hidden">
        {/* 第一列：分类 */}
        <div 
          className="flex-shrink-0 bg-gray-50 border-r border-gray-200 flex flex-col relative"
          style={{ width: columnWidths.category }}
        >
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-100">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">分类</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {(['all_in_one', 'product', 'module'] as ProductCategory[]).map((category) => (
              <button
                key={category}
                onClick={() => onNavigateToCategory(category)}
                className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
                  navigation.category === category
                    ? 'bg-blue-500 text-white'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <div className="flex items-center gap-2">
                  {category === 'all_in_one' ? <Box className="w-4 h-4" /> :
                   category === 'module' ? <Layers className="w-4 h-4" /> :
                   <Package className="w-4 h-4" />}
                  <span>{ProductCategoryLabels[category]}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className={`text-xs ${navigation.category === category ? 'text-blue-100' : 'text-gray-400'}`}>
                    {categorySeriesCount[category]}
                  </span>
                  <ChevronRight className={`w-4 h-4 ${navigation.category === category ? 'text-white' : 'text-gray-300'}`} />
                </div>
              </button>
            ))}
          </div>
          {/* 列宽调整手柄 */}
          <div
            className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 transition-colors z-10"
            onMouseDown={(e) => handleResizeStart('category', e)}
          />
        </div>

        {/* 第二列：系列 */}
        <div 
          className={`flex-shrink-0 bg-white border-r border-gray-200 flex flex-col relative ${!navigation.category ? 'opacity-50' : ''}`}
          style={{ width: columnWidths.series }}
        >
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">系列</span>
            {navigation.category && (
              <button
                onClick={startInlineSeriesCreation}
                className="p-1 hover:bg-gray-200 rounded"
                title="添加系列"
              >
                <Plus className="w-3.5 h-3.5 text-gray-500" />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {!navigation.category ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                请先选择分类
              </div>
            ) : (
              <>
                {currentSeriesList.length === 0 && !isCreatingSeriesInline && (
                  <div className="p-4 text-center text-gray-400 text-sm">
                    暂无系列
                    <button
                      onClick={startInlineSeriesCreation}
                      className="block mx-auto mt-2 text-blue-500 hover:underline"
                    >
                      添加系列
                    </button>
                  </div>
                )}
                {currentSeriesList.map((series) => (
                  <div
                    key={series.id}
                    onClick={() => onNavigateToSeries(series)}
                    onContextMenu={(e) => onContextMenu(e, 'series', series)}
                    onDragOver={(e) => onDragOver(e, series.name)}
                    onDragLeave={onDragLeave}
                    onDrop={(e) => onDrop(e, series)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors cursor-pointer ${
                      navigation.series?.id === series.id
                        ? 'bg-blue-500 text-white'
                        : dropTargetSeries === series.name
                        ? 'bg-blue-100 border-2 border-blue-400 border-dashed'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FolderOpen className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{series.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`text-xs ${navigation.series?.id === series.id ? 'text-blue-100' : 'text-gray-400'}`}>
                        {seriesProductCount[series.name] || 0}
                      </span>
                      <ChevronRight className={`w-4 h-4 flex-shrink-0 ${navigation.series?.id === series.id ? 'text-white' : 'text-gray-300'}`} />
                    </div>
                  </div>
                ))}
                {/* 内联创建系列输入框 */}
                {isCreatingSeriesInline && (
                  <div className="px-3 py-2 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <input
                        ref={newSeriesInputRef}
                        type="text"
                        value={newSeriesName}
                        onChange={(e) => setNewSeriesName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleCreateSeriesInline();
                          } else if (e.key === 'Escape') {
                            cancelInlineSeriesCreation();
                          }
                        }}
                        onBlur={() => {
                          if (newSeriesName.trim()) {
                            handleCreateSeriesInline();
                          } else {
                            cancelInlineSeriesCreation();
                          }
                        }}
                        placeholder="输入系列名称..."
                        className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1 pl-6">
                      按 Enter 保存，Esc 取消
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          {/* 列宽调整手柄 */}
          <div
            className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 transition-colors z-10"
            onMouseDown={(e) => handleResizeStart('series', e)}
          />
        </div>

        {/* 第三列：产品 */}
        <div 
          className={`flex-shrink-0 bg-white border-r border-gray-200 flex flex-col relative ${!navigation.series ? 'opacity-50' : ''}`}
          style={{ width: columnWidths.product }}
        >
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">产品</span>
            {navigation.series && (
              <button
                onClick={onCreateProduct}
                className="p-1 hover:bg-gray-200 rounded"
                title="添加产品"
              >
                <Plus className="w-3.5 h-3.5 text-gray-500" />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {!navigation.series ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                请先选择系列
              </div>
            ) : currentProducts.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                暂无产品
                <button
                  onClick={onCreateProduct}
                  className="block mx-auto mt-2 text-blue-500 hover:underline"
                >
                  添加产品
                </button>
              </div>
            ) : (
              currentProducts.map((product) => (
                <div
                  key={product.id}
                  onClick={() => onNavigateToProduct(product)}
                  onContextMenu={(e) => onContextMenu(e, 'product', product)}
                  draggable
                  onDragStart={(e) => onDragStart(e, product)}
                  onDragEnd={onDragEnd}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors cursor-pointer ${
                    navigation.product?.id === product.id
                      ? 'bg-blue-500 text-white'
                      : draggedProduct?.id === product.id
                      ? 'opacity-50 bg-gray-100'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <div className="w-10 h-10 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-4 h-4 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="font-medium truncate">{product.name}</span>
                      {product.is_featured && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 flex-shrink-0" />}
                    </div>
                    <p className={`text-xs truncate ${navigation.product?.id === product.id ? 'text-blue-100' : 'text-gray-400'}`}>
                      {product.models?.length || 0} 型号
                    </p>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 ${navigation.product?.id === product.id ? 'text-white' : 'text-gray-300'}`} />
                </div>
              ))
            )}
          </div>
          {/* 列宽调整手柄 */}
          <div
            className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 transition-colors z-10"
            onMouseDown={(e) => handleResizeStart('product', e)}
          />
        </div>

        {/* 第四列：产品详情和型号 */}
        <div className="flex-1 bg-white flex flex-col min-w-0">
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">详情</span>
            {navigation.product && (
              <button
                onClick={() => onAddModel(navigation.product!.id)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
              >
                <Plus className="w-3.5 h-3.5" />
                添加型号
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {!navigation.product ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                请选择一个产品查看详情
              </div>
            ) : (
              <ProductDetailContent 
                product={navigation.product}
                onEditProduct={() => onEditProduct(navigation.product!)}
                onAddModel={() => onAddModel(navigation.product!.id)}
                onEditModel={(model) => onEditModel(navigation.product!.id, model)}
                onDeleteModel={onDeleteModel}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// 产品详情内容组件
function ProductDetailContent({
  product,
  onEditProduct,
  onAddModel,
  onEditModel,
  onDeleteModel,
}: {
  product: Product;
  onEditProduct: () => void;
  onAddModel: () => void;
  onEditModel: (model: ProductModel) => void;
  onDeleteModel: (model: ProductModel) => void;
}) {
  return (
    <div className="space-y-4">
      {/* 产品信息卡片 */}
      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex items-start gap-4">
          <div className="w-24 h-24 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="w-8 h-8 text-gray-600" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-lg text-gray-900">{product.name}</h3>
              {product.is_featured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
            </div>
            {product.slogan && (
              <p className="text-sm text-gray-500 italic mb-2">"{product.slogan}"</p>
            )}
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">{product.pitch_range || '—'}</span>
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded">{product.process_type || '—'}</span>
            </div>
          </div>
          <button
            onClick={onEditProduct}
            className="p-2 hover:bg-gray-200 rounded-lg"
            title="编辑产品"
          >
            <Edit2 className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        {product.features && product.features.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-gray-200">
            {product.features.map((f, i) => (
              <span key={i} className="px-2 py-0.5 bg-white text-gray-500 text-xs rounded border">{f}</span>
            ))}
          </div>
        )}
      </div>

      {/* 型号列表 */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">
          产品型号 ({product.models?.length || 0})
        </h4>
        {product.models && product.models.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {product.models.map((model) => (
              <div
                key={model.id}
                className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-blue-200 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm text-gray-900">{model.model_name}</span>
                  {model.is_default && (
                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">默认</span>
                  )}
                </div>
                <div className="space-y-1 text-xs text-gray-500 mb-2">
                  {model.pitch && <p>间距: P{model.pitch}</p>}
                  {model.cabinet_size && <p>箱体: {model.cabinet_size}</p>}
                </div>
                <div className="flex gap-2 pt-2 border-t border-gray-200">
                  <button
                    onClick={() => onEditModel(model)}
                    className="flex-1 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                  >
                    编辑
                  </button>
                  <button
                    onClick={() => onDeleteModel(model)}
                    className="flex-1 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                  >
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <p className="text-gray-400 text-sm mb-2">暂无型号</p>
            <button
              onClick={onAddModel}
              className="text-blue-600 hover:text-blue-700 text-sm"
            >
              添加第一个型号
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
